import { useTranslation } from 'react-i18next';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { CENTRAL_KITCHEN_TABS } from '@/Pages/tabRoutes';
import TransfersPanel from './components/TransfersPanel';
import ProductionPanel from './components/ProductionPanel';
import RequisitionsPanel from './components/RequisitionsPanel';
import ReplenishmentPanel from './components/ReplenishmentPanel';

/**
 * §18 / ADR-0015 — the commissary: batches, transfers with a middle, branch
 * requisitions and replenishment planning.
 *
 * Transfers leads. The correctness idea worth surfacing (ADR-0015 §3) is that
 * stock that has left the commissary and not yet arrived at a branch belongs
 * to nobody and must be accounted for — a real, visible IN_TRANSIT state,
 * decomposed by the transfer holding it, never a gap between two instantaneous
 * movements. A receive that comes up short does not get absorbed: resolving it
 * is a deliberate act with a typed reason, because it is the only step in a
 * transfer's life allowed to change chain-wide stock.
 */
export default function CentralKitchen() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/central-kitchen', CENTRAL_KITCHEN_TABS);

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('centralKitchen.title')}</h1>
        <p className="text-sm text-ink-muted">{t('centralKitchen.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={t('centralKitchen.title')}
        idPrefix="central-kitchen"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {current === 'transfers' && <TransfersPanel />}
      {current === 'production' && <ProductionPanel />}
      {current === 'requisitions' && <RequisitionsPanel />}
      {current === 'replenishment' && <ReplenishmentPanel />}
    </div>
  );
}
